/**
 * Biometric Authentication Service
 * Face ID (iOS) / Fingerprint (Android) for app unlock.
 *
 * Flow:
 *   1. App start → check if token exists in SecureStore
 *   2. If token + biometrics enabled → prompt Face ID / Touch ID
 *   3. If biometric succeeds → validate token via GET /auth/me
 *   4. If biometric fails or cancelled → show login screen
 */
import { Platform } from 'react-native'
import * as LocalAuthentication from 'expo-local-authentication'
import { storage, BIOMETRIC_PROMPTED_KEY } from '../utils/storage'

const BIOMETRICS_ENABLED_KEY = 'biometrics_enabled'

/**
 * Check if device supports biometric authentication
 */
export async function isBiometricsAvailable(): Promise<boolean> {
  if (Platform.OS === 'web') return false

  const compatible = await LocalAuthentication.hasHardwareAsync()
  if (!compatible) return false

  const enrolled = await LocalAuthentication.isEnrolledAsync()
  return enrolled
}

/**
 * Get the type of biometric available (for UI labels)
 */
export async function getBiometricType(): Promise<'face' | 'fingerprint' | 'iris' | null> {
  if (Platform.OS === 'web') return null

  const types = await LocalAuthentication.supportedAuthenticationTypesAsync()
  if (types.includes(LocalAuthentication.AuthenticationType.FACIAL_RECOGNITION)) {
    return 'face'
  }
  if (types.includes(LocalAuthentication.AuthenticationType.FINGERPRINT)) {
    return 'fingerprint'
  }
  if (types.includes(LocalAuthentication.AuthenticationType.IRIS)) {
    return 'iris'
  }
  return null
}

/**
 * Get user-friendly label for biometric type
 */
export function getBiometricLabel(type: 'face' | 'fingerprint' | 'iris' | null): string {
  switch (type) {
    case 'face': return Platform.OS === 'ios' ? 'Face ID' : 'Ansigtsgenkendelse'
    case 'fingerprint': return Platform.OS === 'ios' ? 'Touch ID' : 'Fingeraftryk'
    case 'iris': return 'Iris'
    default: return 'Biometrisk login'
  }
}

/**
 * Prompt user for biometric authentication
 */
export async function authenticateWithBiometrics(): Promise<boolean> {
  if (Platform.OS === 'web') return false

  try {
    const result = await LocalAuthentication.authenticateAsync({
      promptMessage: 'Bekræft din identitet',
      cancelLabel: 'Annuller',
      fallbackLabel: 'Brug adgangskode',
      disableDeviceFallback: false,
    })
    return result.success
  } catch {
    return false
  }
}

/**
 * Check if user has enabled biometric login
 */
export async function isBiometricsEnabled(): Promise<boolean> {
  const val = await storage.get(BIOMETRICS_ENABLED_KEY)
  return val === 'true'
}

/**
 * Enable or disable biometric login
 */
export async function setBiometricsEnabled(enabled: boolean): Promise<void> {
  if (enabled) {
    await storage.set(BIOMETRICS_ENABLED_KEY, 'true')
  } else {
    await storage.remove(BIOMETRICS_ENABLED_KEY)
  }
}

/**
 * Check if user has already been prompted for biometric enrollment post-login.
 * We only prompt once — if they skip, they can still enable via Settings.
 */
export async function hasBeenPromptedForBiometric(): Promise<boolean> {
  const val = await storage.get(BIOMETRIC_PROMPTED_KEY)
  return val === 'true'
}

/**
 * Mark that user has been prompted for biometric enrollment.
 */
export async function markBiometricPrompted(): Promise<void> {
  await storage.set(BIOMETRIC_PROMPTED_KEY, 'true')
}
